import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ColorPicker from '../Replan/components/ColorPicker';

// DnD for param ordering
import { DndContext, closestCenter, useDroppable, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SortableParamItem from '../ShapeList/SortableParamItem';
import { resolveAllParams, math } from '../ShapeList/paramUtils';
import * as ROLES from '../../constants/roles';

// SVG Icons
import { ReactComponent as ProjectIcon } from '../../svg/tree-project.svg';
import { ReactComponent as OrgIcon } from '../../svg/tree-org.svg';
import { ReactComponent as DeleteIcon } from '../../svg/tree-delete.svg';
import { ReactComponent as AddIcon } from '../../svg/tree-add.svg';
import { ReactComponent as ChevronRightIcon } from '../../svg/tree-chevron-right.svg';
import { ReactComponent as ChevronDownIcon } from '../../svg/tree-chevron-down.svg';
import { ReactComponent as CheckIcon } from '../../svg/tree-check.svg';
import { ReactComponent as TagIcon } from '../../svg/tag.svg';

// Custom inline SVG icons for premium visual state badges and publish/import operations
const ShieldIcon = (props) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const LockIcon = (props) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const PublishIcon = (props) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const ImportIcon = (props) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const deepCopy = obj => JSON.parse(JSON.stringify(obj));

/**
 * True when the assembly's document ID does not match the normalized form
 * of its display name — i.e. a legacy timestamp-ID assembly that cannot be
 * renamed (only its params can be edited).
 */
function isLegacyAssembly(a, firebase) {
  if (!a || !firebase || typeof firebase.normalizeAssemblyId !== 'function') return false;
  const normalizedName = firebase.normalizeAssemblyId(a.name || '');
  return normalizedName && normalizedName !== a.id;
}

/**
 * Ensure the default param keys (Assembly, Color, Reference) are present
 * in the params object and appear in the order array.
 */
function ensureDefaultParams(params, assemblyId) {
  if (!params) params = {};
  // Only use defaults when the key is missing or falsy/empty.
  // Preserve any user-entered value.
  const finalAssembly = params.Assembly || assemblyId || '';
  const finalColor = params.Color || '#000000';
  const finalReference = params.Reference || '';

  // Spread existing params first, then enforce the computed defaults
  // so that existing non-empty values are kept and missing/empty ones
  // get the fallback.
  const updated = {
    ...params,
    Assembly: finalAssembly,
    Color: finalColor,
    Reference: finalReference,
  };

  // Ensure order array includes the default keys (preserving existing order)
  const order = Array.isArray(updated.order) ? [...updated.order] : [];
  const defaultOrderKeys = ['Assembly', 'Color', 'Reference'];
  defaultOrderKeys.forEach((key) => {
    if (!order.includes(key)) {
      order.unshift(key);
    }
  });
  updated.order = order;
  return updated;
}

// --- Helper function to convert a string to a deterministic hex color ---
function stringToHexColor(str) {
  if (typeof str !== 'string') {
    str = String(str);
  }
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF;
    color += ('00' + value.toString(16)).substr(-2);
  }

  return color;
}

// --- Helper function to resolve the color of an assembly ---
function getAssemblyColor(a) {
  if (!a) return null;
  let params = a.params || {};
  if (a.objects && a.objects.length > 0 && a.objects[0].params) {
    params = a.objects[0].params;
  }
  if (!params || params.Color === undefined) return null;
  const v = params.Color;
  try {
    const resolved = resolveAllParams(params, {});
    let display = resolved.Color !== undefined ? resolved.Color : v;
    const isHexColor = typeof display === 'string' && /^#[0-9A-F]{6}$/i.test(display);
    if (typeof v === 'string' && v.startsWith('=') && !isHexColor && !display?.error) {
      display = stringToHexColor(String(display));
    }
    return (typeof display === 'string' && /^#[0-9A-F]{6}$/i.test(display)) ? display : stringToHexColor(String(display || v || '#000000'));
  } catch (err) {
    return (typeof v === 'string' && /^#[0-9A-F]{6}$/i.test(v)) ? v : stringToHexColor(String(v || '#000000'));
  }
}

// --- Helper function to resolve the category of an assembly ---
function getAssemblyCategory(a) {
  if (!a) return '';
  let params = a.params || {};
  if (a.objects && a.objects.length > 0 && a.objects[0].params) {
    params = a.objects[0].params;
  }
  return params.Category || a.category || '';
}

const DEFAULT_ASSEMBLIES = [];

const promiseWithTimeout = (promise, ms, timeoutErrorValue) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(timeoutErrorValue || 'Timeout')), ms)
    )
  ]);
};

const AssemblyManager = ({
  firebase,
  projectName,
  organization, // Fallback organization ID
  organizationId, // Primary organization ID
  authUser,
  onAssembliesChange,
  onClose,
  // Fallbacks for non-firebase prop-driven mode (if used)
  assemblies = DEFAULT_ASSEMBLIES,
  onSave,
  onDelete,
}) => {
  const { t } = useTranslation();

  // Resolve Org ID from props with a fallback to the user's first organization role
  let orgId = organizationId || organization;
  if (!orgId && authUser?.roles) {
    const userOrgs = Object.keys(authUser.roles).filter(key => authUser.roles[key] !== null);
    if (userOrgs.length > 0) {
      orgId = userOrgs[0];
    }
  }

  // Determine if user is admin
  const isAdmin = authUser?.roles?.[orgId] === ROLES.ADMIN || authUser?.roles?.[orgId] === 'admin' || authUser?.isAdmin;

  // Tabs & Lists State
  const [activeTab, setActiveTab] = useState('project'); // 'project' | 'org'
  const [projectAssemblies, setProjectAssemblies] = useState([]);
  const [orgAssemblies, setOrgAssemblies] = useState([]);

  const [local, setLocal] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedOriginalId, setSelectedOriginalId] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [isResizing, setIsResizing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [paramSearchTerm, setParamSearchTerm] = useState('');
  const [editingParam, setEditingParam] = useState(null);
  const [addingParamFor, setAddingParamFor] = useState(null); // tracks objectIndex currently adding param
  const [newParamInputs, setNewParamInputs] = useState({ key: '', value: '' });
  const [isCreatingAssembly, setIsCreatingAssembly] = useState(false);
  const [newAssemblyName, setNewAssemblyName] = useState('');
  const [isRenamingAssembly, setIsRenamingAssembly] = useState(false);
  const [addingGroupFor, setAddingGroupFor] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [editingGroupName, setEditingGroupName] = useState(null);

  // Loading & Action Statuses
  const [loading, setLoading] = useState(false);
  const [savingStatus, setSavingStatus] = useState('idle'); // 'idle' | 'saving' | 'success' | 'error'
  const [importingStatus, setImportingStatus] = useState('idle'); // 'idle' | 'importing' | 'success' | 'error'
  const [publishingStatus, setPublishingStatus] = useState('idle'); // 'idle' | 'publishing' | 'success' | 'error'

  const deletedIdsRef = useRef(new Set());

  // Trigger fetch on mount or key props changes
  const prevDeps = useRef({});
  useEffect(() => {
    const changes = {};
    const currentDeps = { firebase: !!firebase, projectName, orgId, activeTab, assembliesLength: assemblies?.length };
    Object.keys(currentDeps).forEach(key => {
      if (prevDeps.current[key] !== currentDeps[key]) {
        changes[key] = { from: prevDeps.current[key], to: currentDeps[key] };
      }
    });
    console.log('[AssemblyManager useEffect triggered] Changes:', changes);
    prevDeps.current = currentDeps;

    const fetchAssemblies = async () => {
      if (!firebase) {
        console.log('[AssemblyManager fetchAssemblies] Firebase is not defined, skipping');
        return;
      }
      console.log('[AssemblyManager fetchAssemblies] Starting fetch. orgId:', orgId, 'projectName:', projectName);
      setLoading(true);
      try {
        let pAsms = [];
        let oAsms = [];

        if (orgId) {
          if (projectName) {
            console.log('[AssemblyManager fetchAssemblies] Calling doGetProjectAssemblies');
            const projectSnap = await promiseWithTimeout(
              firebase.doGetProjectAssemblies(orgId, projectName),
              5000,
              'doGetProjectAssemblies timed out'
            );
            pAsms = projectSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            console.log('[AssemblyManager fetchAssemblies] Got project assemblies:', pAsms.length);
          } else {
            console.log('[AssemblyManager fetchAssemblies] projectName is falsy, skipping project fetch');
          }

          console.log('[AssemblyManager fetchAssemblies] Calling doGetOrgAssemblies');
          const orgSnap = await promiseWithTimeout(
            firebase.doGetOrgAssemblies(orgId),
            5000,
            'doGetOrgAssemblies timed out'
          );
          oAsms = orgSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log('[AssemblyManager fetchAssemblies] Got org assemblies:', oAsms.length);
        } else {
          console.log('[AssemblyManager fetchAssemblies] orgId is falsy, skipping queries');
        }

        setProjectAssemblies(pAsms);
        setOrgAssemblies(oAsms);

        const currentList = activeTab === 'project' ? pAsms : oAsms;
        setLocal(currentList.map(a => deepCopy(a)));

        // Auto-select first item if none is selected
        if (currentList.length > 0) {
          setSelectedId(currentList[0].id);
        } else {
          setSelectedId(null);
        }
      } catch (err) {
        console.error('[AssemblyManager fetchAssemblies] Error fetching assemblies:', err);
      } finally {
        console.log('[AssemblyManager fetchAssemblies] Fetch completed, setting loading to false');
        setLoading(false);
      }
    };

    if (firebase) {
      fetchAssemblies();
    } else {
      console.log('[AssemblyManager fetchAssemblies] No firebase prop, using assemblies fallback');
      // Fallback mode: use assemblies passed as prop
      const incomingIds = assemblies.map(a => a.id).filter(id => !deletedIdsRef.current.has(id)).sort().join(',');
      const localIds = local.map(a => a.id).sort().join(',');
      if (incomingIds !== localIds) {
        const filtered = assemblies.filter(a => !deletedIdsRef.current.has(a.id));
        setLocal(filtered.map(a => deepCopy(a)));
        if (filtered.length > 0 && !selectedId) setSelectedId(filtered[0].id);
      }
    }
  }, [firebase, projectName, orgId, activeTab, assemblies]);

  // Track original ID for rename detection
  useEffect(() => {
    setSelectedOriginalId(selectedId);
  }, [selectedId]);

  // Reset add-param state when switching assemblies
  useEffect(() => {
    setAddingParamFor(null);
    setNewParamInputs({ key: '', value: '' });
    setAddingGroupFor(null);
    setNewGroupName('');
    setCollapsedGroups({});
  }, [selectedId]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = e.clientX;
      if (newWidth >= 200 && newWidth <= 800) setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => setIsResizing(false);
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const updateAssembly = (id, patch) => setLocal(prev => prev.map(a => (a.id === id ? { ...a, ...patch } : a)));

  const updateObjectParams = (assemblyId, objectIndex, newParams) => {
    setLocal(prev => prev.map(a => {
      if (a.id !== assemblyId) return a;
      const objs = a.objects ? [...a.objects] : [{ name: a.name || 'params', params: a.params || {} }];
      objs[objectIndex] = { ...objs[objectIndex], params: newParams };
      return a.objects ? { ...a, objects: objs } : { ...a, params: objs[0].params };
    }));
  };

  const handleParamChange = (assemblyId, objectIndex, key, value) => {
    const assembly = local.find(a => a.id === assemblyId);
    if (!assembly) return;
    const objs = assembly.objects ? deepCopy(assembly.objects) : [{ params: deepCopy(assembly.params || {}) }];
    const params = { ...(objs[objectIndex].params || {}) };
    params[key] = value;

    // Ensure order array exists and includes this key
    const existingOrder = Array.isArray(params.order) ? [...params.order] : Object.keys(params).filter(k => k !== 'order');
    if (!existingOrder.includes(key)) existingOrder.push(key);
    params.order = existingOrder;

    objs[objectIndex].params = params;
    updateObjectParams(assemblyId, objectIndex, params);
  };

  const handleAddParam = (assemblyId, objectIndex) => {
    const { key, value } = newParamInputs;
    if (!key || !key.trim()) return;
    handleParamChange(assemblyId, objectIndex, key.trim(), value.trim());
    setNewParamInputs({ key: '', value: '' });
    setAddingParamFor(null);
  };

  const handleDeleteParam = (assemblyId, objectIndex, key) => {
    const assembly = local.find(a => a.id === assemblyId);
    if (!assembly) return;
    const objs = assembly.objects ? deepCopy(assembly.objects) : [{ params: deepCopy(assembly.params || {}) }];
    const params = { ...(objs[objectIndex].params || {}) };
    delete params[key];
    if (Array.isArray(params.order)) params.order = params.order.filter(k => k !== key);
    // Remove the key from any group it belonged to so the saved data holds
    // no dangling references to a deleted param.
    if (params.Groups) {
      Object.keys(params.Groups).forEach((groupName) => {
        if (Array.isArray(params.Groups[groupName])) {
          params.Groups[groupName] = params.Groups[groupName].filter(k => k !== key);
        }
      });
    }
    updateObjectParams(assemblyId, objectIndex, params);
  };

  const handleAddGroup = (assemblyId, objectIndex) => {
    if (!newGroupName || !newGroupName.trim()) return;
    const groupName = newGroupName.trim();
    const assembly = local.find(a => a.id === assemblyId);
    if (!assembly) return;
    const objs = assembly.objects ? deepCopy(assembly.objects) : [{ params: deepCopy(assembly.params || {}) }];
    const params = { ...(objs[objectIndex].params || {}) };

    if (!params.Groups) {
      params.Groups = {};
    }

    if (params.Groups[groupName] !== undefined || params[groupName] !== undefined) {
      alert(t('assemblyManager.paramOrGroupExists'));
      return;
    }

    params.Groups[groupName] = []; // Empty array for param keys

    const existingOrder = Array.isArray(params.order) ? [...params.order] : Object.keys(params).filter(k => k !== 'order' && k !== 'Groups');
    if (!existingOrder.includes(groupName)) existingOrder.push(groupName);
    params.order = existingOrder;

    updateObjectParams(assemblyId, objectIndex, params);
    setNewGroupName('');
    setAddingGroupFor(null);
    setCollapsedGroups(prev => ({ ...prev, [groupName]: true }));
  };

  const handleDeleteGroup = (assemblyId, objectIndex, groupKey) => {
    const assembly = local.find(a => a.id === assemblyId);
    if (!assembly) return;
    const objs = assembly.objects ? deepCopy(assembly.objects) : [{ params: deepCopy(assembly.params || {}) }];
    const params = { ...(objs[objectIndex].params || {}) };

    if (params.Groups && params.Groups[groupKey]) {
      delete params.Groups[groupKey];
      if (Object.keys(params.Groups).length === 0) {
        delete params.Groups;
      }
    }

    if (Array.isArray(params.order)) {
      params.order = params.order.filter(k => k !== groupKey);
    }

    updateObjectParams(assemblyId, objectIndex, params);
    setCollapsedGroups(prev => {
      const newState = { ...prev };
      delete newState[groupKey];
      return newState;
    });
  };

  const handleRenameGroup = (assemblyId, objectIndex, oldGroupKey, newName) => {
    if (!newName || !newName.trim()) return;
    const newKey = newName.trim();
    if (oldGroupKey === newKey) return;

    const assembly = local.find(a => a.id === assemblyId);
    if (!assembly) return;
    const objs = assembly.objects ? deepCopy(assembly.objects) : [{ params: deepCopy(assembly.params || {}) }];
    const params = { ...(objs[objectIndex].params || {}) };

    if (params[newKey] !== undefined || (params.Groups && params.Groups[newKey] !== undefined)) {
      alert(t('assemblyManager.paramOrGroupExists'));
      return;
    }

    if (params.Groups && params.Groups[oldGroupKey]) {
      params.Groups[newKey] = params.Groups[oldGroupKey];
      delete params.Groups[oldGroupKey];

      if (Array.isArray(params.order)) {
        const idx = params.order.indexOf(oldGroupKey);
        if (idx !== -1) {
          params.order[idx] = newKey;
        }
      }

      updateObjectParams(assemblyId, objectIndex, params);

      setCollapsedGroups(prev => {
        const newState = { ...prev };
        if (prev[oldGroupKey] !== undefined) {
          newState[newKey] = prev[oldGroupKey];
          delete newState[oldGroupKey];
        }
        return newState;
      });
    }
  };

  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // Add assembly: either project or organization level depending on tab
  const handleAddAssembly = async () => {
    if (!newAssemblyName.trim()) return;

    // Validate name
    if (firebase && firebase.assemblyNameError) {
      const nameErr = firebase.assemblyNameError(newAssemblyName);
      if (nameErr) {
        alert(nameErr);
        return;
      }
    }

    const normalizedId = firebase
      ? firebase.normalizeAssemblyId(newAssemblyName)
      : newAssemblyName.trim();
    if (!normalizedId) {
      alert('Invalid assembly name after normalization.');
      return;
    }

    // Check for duplicate in local list (by normalized ID)
    const alreadyExists = local.some(
      (a) =>
        a.id === normalizedId ||
        (firebase && firebase.normalizeAssemblyId(a.id) === normalizedId),
    );
    if (alreadyExists) {
      alert(
        t(
          'assemblyManager.assemblyNameExists',
          'An assembly with this name already exists.',
        ),
      );
      return;
    }

    const newAssembly = {
      id: normalizedId,
      name: normalizedId,
      params: ensureDefaultParams({}, normalizedId),
    };

    setLocal((prev) => [...prev, newAssembly]);
    setSelectedId(normalizedId);
    setNewAssemblyName('');
    setIsCreatingAssembly(false);

    if (firebase) {
      try {
        setSavingStatus('saving');
        if (activeTab === 'project') {
          await firebase.doCreateAssembly(
            orgId,
            projectName,
            normalizedId,
            {},
            normalizedId,
          );
          const projectSnap = await firebase.doGetProjectAssemblies(
            orgId,
            projectName,
          );
          const pAsms = projectSnap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setProjectAssemblies(pAsms);
          onAssembliesChange && onAssembliesChange(pAsms);
        } else {
          await firebase.doCreateOrgAssembly(
            orgId,
            normalizedId,
            {},
            normalizedId,
          );
          const orgSnap = await firebase.doGetOrgAssemblies(orgId);
          setOrgAssemblies(
            orgSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          );
        }
        setSavingStatus('success');
      } catch (err) {
        console.error('Error creating assembly in database:', err);
        if (err.code === 'ASSEMBLY_ALREADY_EXISTS') {
          alert(
            t(
              'assemblyManager.assemblyNameExists',
              'An assembly with this name already exists.',
            ),
          );
        } else if (err.code === 'INVALID_ASSEMBLY_NAME') {
          alert(err.message);
        } else {
          alert(
            t('assemblyManager.saveFailed', 'Failed to save assembly: {{error}}', {
              error: err.message,
            }),
          );
        }
        setSavingStatus('error');
        // Remove the optimistic local entry on failure
        setLocal((prev) => prev.filter((a) => a.id !== normalizedId));
        if (selectedId === normalizedId) {
          setSelectedId(local.length > 0 ? local[0].id : null);
        }
      } finally {
        setTimeout(() => setSavingStatus('idle'), 1500);
      }
    } else {
      // Prop fallback
      onSave && onSave(newAssembly);
    }
  };

  const handleCancelCreateAssembly = () => {
    setIsCreatingAssembly(false);
    setNewAssemblyName('');
  };

  const handleDeleteObject = (assemblyId, objectIndex) => {
    setLocal(prev => prev.map(a => {
      if (a.id !== assemblyId) return a;
      if (!a.objects) return a;
      const objs = a.objects.filter((_, i) => i !== objectIndex);
      return { ...a, objects: objs };
    }));
  };

  const formatResolved = (val) => {
    if (val == null) return '';
    if (val?.error) return val.error;
    if (val && val.isUnit) {
      try { return math.format(val, { notation: 'fixed', precision: 1 }); } catch { return String(val); }
    }
    if (typeof val === 'number') return val.toFixed(1);
    return String(val);
  };

  // 2. Save edited assembly (to Firestore or Callback)
  const handleSaveEdited = async (selectedAssembly) => {
    if (!selectedAssembly) return;
    if (firebase) {
      try {
        setSavingStatus('saving');
        const originalId = selectedOriginalId || selectedAssembly.id;
        const name = selectedAssembly.name;
        // Edits live in objects[i].params when the `objects` structure is used.
        // Write the edited params exactly as shown in the editor: params the
        // user deleted (including Assembly/Color/Reference) must stay deleted,
        // so no default re-adding happens on the save path.
        const params =
          selectedAssembly.objects && selectedAssembly.objects.length > 0
            ? deepCopy(selectedAssembly.objects[0].params || {})
            : deepCopy(selectedAssembly.params || {});
        const newNormalizedId = firebase.normalizeAssemblyId(name);
        const isRename = newNormalizedId && newNormalizedId !== originalId;

        if (isRename) {
          // Block rename of legacy assemblies (timestamp-ID docs)
          if (isLegacyAssembly(selectedAssembly, firebase)) {
            alert(
              t(
                'assemblyManager.legacyRenameBlocked',
                'This is a legacy assembly and cannot be renamed. You can edit its parameters but the document ID must stay unchanged.',
              ),
            );
            setSavingStatus('idle');
            return;
          }
          // params.Assembly must match the new doc ID
          params.Assembly = newNormalizedId;
          // Check for name conflict in target namespace
          if (activeTab === 'project') {
            const result = await firebase.doRenameAssembly(
              orgId,
              projectName,
              originalId,
              name,
              params,
              name,
            );

            // Update local state with new ID
            setLocal((prev) =>
              prev.map((a) =>
                a.id === originalId ? { ...a, id: result.newId, name: result.newId } : a,
              ),
            );
            setSelectedId(result.newId);

            const projectSnap = await firebase.doGetProjectAssemblies(
              orgId,
              projectName,
            );
            const pAsms = projectSnap.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setProjectAssemblies(pAsms);
            onAssembliesChange && onAssembliesChange(pAsms);
          } else {
            const result = await firebase.doRenameOrgAssembly(
              orgId,
              originalId,
              name,
              params,
              name,
            );

            setLocal((prev) =>
              prev.map((a) =>
                a.id === originalId ? { ...a, id: result.newId, name: result.newId } : a,
              ),
            );
            setSelectedId(result.newId);

            const orgSnap = await firebase.doGetOrgAssemblies(orgId);
            setOrgAssemblies(
              orgSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
            );
          }
        } else {
          // Same name — do update (not create)
          if (activeTab === 'project') {
            await firebase.doUpdateAssembly(
              orgId,
              projectName,
              originalId,
              params,
              name,
              null,
              null,
              { deepDeleteRemoved: true },
            );
            const projectSnap = await firebase.doGetProjectAssemblies(
              orgId,
              projectName,
            );
            const pAsms = projectSnap.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setProjectAssemblies(pAsms);
            onAssembliesChange && onAssembliesChange(pAsms);
          } else {
            await firebase.doUpdateOrgAssembly(
              orgId,
              originalId,
              params,
              name,
              null,
              null,
              { deepDeleteRemoved: true },
            );
            const orgSnap = await firebase.doGetOrgAssemblies(orgId);
            setOrgAssemblies(
              orgSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
            );
          }
        }
        setSavingStatus('success');
      } catch (err) {
        console.error('Error saving assembly:', err);
        if (err.code === 'ASSEMBLY_ALREADY_EXISTS') {
          alert(
            t(
              'assemblyManager.renameConflict',
              'An assembly with this name already exists. Rename cancelled.',
            ),
          );
        } else {
          alert(
            t('assemblyManager.saveFailed', 'Failed to save assembly: {{error}}', {
              error: err.message,
            }),
          );
        }
        setSavingStatus('error');
      } finally {
        setTimeout(() => setSavingStatus('idle'), 1500);
      }
    } else {
      // Prop fallback
      onSave && onSave(selectedAssembly);
    }
  };

  // Delete assembly
  const handleDelete = async (id) => {
    deletedIdsRef.current.add(id);

    if (selectedId === id) {
      const remaining = local.filter(a => a.id !== id);
      setSelectedId(remaining.length > 0 ? remaining[0].id : null);
    }

    setLocal(prev => prev.filter(a => a.id !== id));

    if (firebase) {
      try {
        setSavingStatus('saving');
        if (activeTab === 'project') {
          await firebase.doDeleteAssembly(orgId, projectName, id);

          // Refresh project assemblies
          const projectSnap = await firebase.doGetProjectAssemblies(orgId, projectName);
          const pAsms = projectSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setProjectAssemblies(pAsms);
          onAssembliesChange && onAssembliesChange(pAsms);
        } else {
          await firebase.doDeleteOrgAssembly(orgId, id);

          // Refresh org assemblies
          const orgSnap = await firebase.doGetOrgAssemblies(orgId);
          setOrgAssemblies(orgSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }
        setSavingStatus('success');
      } catch (err) {
        console.error('Error deleting assembly from database:', err);
        setSavingStatus('error');
      } finally {
        setTimeout(() => setSavingStatus('idle'), 1500);
      }
    } else {
      // Prop fallback
      onDelete && onDelete(id);
    }
  };

  // 3. Import assembly to Project catalog
  const handleImportToProject = async (orgAssembly) => {
    if (!orgAssembly || !firebase) return;
    try {
      setImportingStatus('importing');
      const assemblyId = orgAssembly.id;
      const name = orgAssembly.name;
      const normalizedId = firebase.normalizeAssemblyId(name) || assemblyId;
      const params = ensureDefaultParams(
        deepCopy(orgAssembly.params || {}),
        normalizedId,
      );

      // Check if project already has an assembly with the same normalized name
      // Match by doc ID or by normalized name (for legacy assemblies whose name may differ from ID)
      const existing = projectAssemblies.find(
        (a) =>
          a.id === normalizedId ||
          firebase.normalizeAssemblyId(a.name) === normalizedId,
      );
      if (existing) {
        const shouldOverwrite = window.confirm(
          t(
            'assemblyManager.overwritePrompt',
            'Assembly "{{name}}" already exists in this project. Do you want to overwrite it?',
            { name: normalizedId },
          ),
        );
        if (!shouldOverwrite) {
          setImportingStatus('idle');
          return;
        }
        await firebase.doUpdateAssembly(
          orgId,
          projectName,
          normalizedId,
          params,
          name,
          null,
          null,
          { deepDeleteRemoved: true },
        );
      } else {
        await firebase.doCreateAssembly(
          orgId,
          projectName,
          normalizedId,
          params,
          name,
        );
      }

      // Refresh project list
      const projectSnap = await firebase.doGetProjectAssemblies(orgId, projectName);
      const pAsms = projectSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjectAssemblies(pAsms);
      onAssembliesChange && onAssembliesChange(pAsms);

      setImportingStatus('success');
      setTimeout(() => {
        setImportingStatus('idle');
      }, 1000);
    } catch (err) {
      console.error('Error importing assembly:', err);
      setImportingStatus('error');
      alert(t('assemblyManager.importFailed', { error: err.message }, 'Failed to import template to project: {{error}}'));
    }
  };

  // 4. Publish project assembly to Org templates
  const handlePublishToOrganization = async (projectAssembly) => {
    if (!projectAssembly || !firebase) return;
    try {
      setPublishingStatus('publishing');
      const assemblyId = projectAssembly.id;
      const name = projectAssembly.name;
      const normalizedId = firebase.normalizeAssemblyId(name) || assemblyId;
      const params = ensureDefaultParams(
        deepCopy(projectAssembly.params || {}),
        normalizedId,
      );

      // Check if org already has an assembly with the same normalized name
      // Match by doc ID or by normalized name (for legacy assemblies whose name may differ from ID)
      const existing = orgAssemblies.find(
        (a) =>
          a.id === normalizedId ||
          firebase.normalizeAssemblyId(a.name) === normalizedId,
      );
      if (existing) {
        const shouldOverwrite = window.confirm(
          t(
            'assemblyManager.overwritePrompt',
            'Assembly "{{name}}" already exists in the organization. Do you want to overwrite it?',
            { name: normalizedId },
          ),
        );
        if (!shouldOverwrite) {
          setPublishingStatus('idle');
          return;
        }
        await firebase.doUpdateOrgAssembly(
          orgId,
          normalizedId,
          params,
          name,
          null,
          null,
          { deepDeleteRemoved: true },
        );
      } else {
        await firebase.doCreateOrgAssembly(
          orgId,
          normalizedId,
          params,
          name,
        );
      }

      // Refresh org templates
      const orgSnap = await firebase.doGetOrgAssemblies(orgId);
      setOrgAssemblies(orgSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      setPublishingStatus('success');
      setTimeout(() => {
        setPublishingStatus('idle');
      }, 1000);
    } catch (err) {
      console.error('Error publishing assembly:', err);
      setPublishingStatus('error');
      alert(t('assemblyManager.publishFailed', { error: err.message }, 'Failed to publish assembly to organization templates: {{error}}'));
    }
  };

  // Render a parameter row
  const renderParamRow = (assemblyId, objectIndex, k, v, allParams) => {
    const isEditing = editingParam?.key === k && editingParam?.objectIndex === objectIndex;

    if (k === 'Color') {
      const resolved = resolveAllParams(allParams || {}, {});
      let display = resolved[k] !== undefined ? resolved[k] : v;
      const isHexColor = typeof display === 'string' && /^#[0-9A-F]{6}$/i.test(display);
      if (typeof v === 'string' && v.startsWith('=') && !isHexColor && !display?.error) {
        display = stringToHexColor(String(display));
      }
      const resolvedColor = (typeof display === 'string' && /^#[0-9A-F]{6}$/i.test(display)) ? display : stringToHexColor(String(display || v || '#000000'));

      return (
        <SortableParamItem key={k} id={k}>
          <div className="param-row">
            <span className="param-key">{k}:</span>
            <div
              className="param-value-container"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flex: 1,
              }}
            >
              <span
                className="param-value read-only"
                style={{ flex: 1, cursor: 'default' }}
                title={
                  typeof v === 'string' && v.startsWith('=')
                    ? `${t('assemblyManager.formula', 'Formula')}: ${v}`
                    : ''
                }
              >
                {v || t('assemblyManager.empty')}
              </span>
              <ColorPicker
                value={resolvedColor}
                onChange={(newColor) => {
                  handleParamChange(assemblyId, objectIndex, k, newColor);
                  setEditingParam(null);
                }}
                title={t('assemblyManager.selectColor')}
                autoOpen={false}
                size="small"
              />
            </div>
            <button className="delete-button" onClick={() => handleDeleteParam(assemblyId, objectIndex, k)} title={`${t('delete')}\n${t('assemblyManager.deleteParamDesc')}`}>✕</button>
          </div>
        </SortableParamItem>
      );
    }

    // Generic param
    const resolved = resolveAllParams(allParams || {}, {});
    const computed = (typeof v === 'string' && v.startsWith('=')) ? resolved[k] : undefined;

    return (
      <SortableParamItem key={k} id={k}>
        <div className="param-row">
          <span className="param-key">{k}:</span>
          {isEditing ? (
            <input
              type="text"
              className="param-value"
              value={editingParam.value}
              onChange={(e) => setEditingParam({ ...editingParam, value: e.target.value })}
              onBlur={() => {
                handleParamChange(assemblyId, objectIndex, k, editingParam.value);
                setEditingParam(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleParamChange(assemblyId, objectIndex, k, editingParam.value);
                  setEditingParam(null);
                }
                if (e.key === 'Escape') setEditingParam(null);
              }}
              autoFocus
            />
          ) : (
            <span
              className="param-value"
              onClick={() => setEditingParam({ key: k, value: v, objectIndex })}
            >
              {v || t('assemblyManager.empty')}
              {computed !== undefined && !computed?.error && (
                <span className="param-computed"> → {formatResolved(computed)}</span>
              )}
            </span>
          )}
          <button className="delete-button" onClick={() => handleDeleteParam(assemblyId, objectIndex, k)} title={`${t('delete')}\n${t('assemblyManager.deleteParamDesc')}`}>✕</button>
        </div>
      </SortableParamItem>
    );
  };

  // Render a read-only parameter row
  const renderReadOnlyParamRow = (k, v, allParams) => {
    if (k === 'Color') {
      const resolved = resolveAllParams(allParams || {}, {});
      let display = resolved[k] !== undefined ? resolved[k] : v;
      const isHexColor = typeof display === 'string' && /^#[0-9A-F]{6}$/i.test(display);
      if (typeof v === 'string' && v.startsWith('=') && !isHexColor && !display?.error) {
        display = stringToHexColor(String(display));
      }
      const resolvedColor = (typeof display === 'string' && /^#[0-9A-F]{6}$/i.test(display)) ? display : stringToHexColor(String(display || v || '#000000'));

      return (
        <div key={k} className="param-row param-row-readonly">
          <span className="param-key">{k}:</span>
          <div className="param-value assembly-color-swatch-wrapper" style={{ cursor: 'default' }}>
            <span>{v || t('assemblyManager.empty')}</span>
            <span
              aria-hidden="true"
              className="color-swatch-indicator"
              style={{ backgroundColor: resolvedColor }}
            />
          </div>
        </div>
      );
    }

    const resolved = resolveAllParams(allParams || {}, {});
    const computed = (typeof v === 'string' && v.startsWith('=')) ? resolved[k] : undefined;

    return (
      <div key={k} className="param-row param-row-readonly">
        <span className="param-key">{k}:</span>
        <span className="param-value" style={{ cursor: 'default' }}>
          {v || t('assemblyManager.empty')}
          {computed !== undefined && !computed?.error && (
            <span className="param-computed"> → {formatResolved(computed)}</span>
          )}
        </span>
      </div>
    );
  };

  // Droppable Group Component
  const DroppableGroup = ({ groupKey, groupParamKeys, assemblyId, objectIndex, allParams }) => {
    const isCollapsed = collapsedGroups[groupKey] ?? true;
    const isEditingName = editingGroupName === groupKey;

    const {
      attributes,
      listeners,
      setNodeRef: setSortableRef,
      transform,
      transition,
      isDragging
    } = useSortable({ id: groupKey });

    const { setNodeRef: setDroppableRef, isOver } = useDroppable({
      id: groupKey,
    });

    const setNodeRef = (node) => {
      setSortableRef(node);
      setDroppableRef(node);
    };

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.3 : 1,
      backgroundColor: isOver ? 'rgba(99, 102, 241, 0.15)' : undefined,
      cursor: isDragging ? 'grabbing' : 'default',
    };

    const paramKeys = groupParamKeys || [];
    const visibleParamKeys = paramKeys.filter(k =>
      (k || '').toLowerCase().includes(paramSearchTerm.toLowerCase()) ||
      String(allParams[k] || '').toLowerCase().includes(paramSearchTerm.toLowerCase())
    );

    return (
      <div ref={setNodeRef} className="assembly-param-group" style={style}>
        <div className={`assembly-param-group-header ${isCollapsed ? 'assembly-param-group-header-collapsed' : 'assembly-param-group-header-expanded'}`}>
          <button
            onClick={() => toggleGroupCollapse(groupKey)}
            className="assembly-group-toggle-btn"
          >
            {isCollapsed ? (
              <ChevronRightIcon className="tool-icon stroke-icon" style={{ width: '10px', height: '10px' }} />
            ) : (
              <ChevronDownIcon className="tool-icon stroke-icon" style={{ width: '10px', height: '10px' }} />
            )}
          </button>
          <span
            {...attributes}
            {...listeners}
            className="assembly-drag-handle"
            title={`${t('assemblyManager.dragToReorder')}\n${t('assemblyManager.dragToReorderDesc')}`}
          >
            ⋮⋮
          </span>
          {isEditingName ? (
            <input
              type="text"
              value={groupKey}
              onChange={(e) => handleRenameGroup(assemblyId, objectIndex, groupKey, e.target.value)}
              onBlur={() => setEditingGroupName(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                  setEditingGroupName(null);
                }
              }}
              autoFocus
              className="assembly-group-rename-input"
            />
          ) : (
            <strong
              onClick={() => setEditingGroupName(groupKey)}
              className="assembly-group-title-editable"
              title={`${t('assemblyManager.clickToRename')}\n${t('assemblyManager.clickToRenameGroupDesc')}`}
            >
              {groupKey}
            </strong>
          )}
          <span className="assembly-param-group-count">({visibleParamKeys.length})</span>
          <button
            className="delete-button"
            onClick={() => handleDeleteGroup(assemblyId, objectIndex, groupKey)}
            title={`${t('assemblyManager.deleteGroupTitle')}\n${t('assemblyManager.deleteGroupDesc')}`}
          >
            ✕
          </button>
        </div>
        {!isCollapsed && (
          <div className="assembly-param-group-content" style={{ minHeight: '20px' }}>
            {visibleParamKeys.length === 0 ? (
              <div className="assembly-param-group-empty">
                {t('assemblyManager.dropParamsHere')}
              </div>
            ) : (
              visibleParamKeys.map(k => renderParamRow(assemblyId, objectIndex, k, allParams[k], allParams))
            )}
          </div>
        )}
      </div>
    );
  };

  // Read-only Group Component
  const ReadOnlyGroup = ({ groupKey, groupParamKeys, allParams }) => {
    const isCollapsed = collapsedGroups[groupKey] ?? true;
    const paramKeys = groupParamKeys || [];
    const visibleParamKeys = paramKeys.filter(k =>
      (k || '').toLowerCase().includes(paramSearchTerm.toLowerCase()) ||
      String(allParams[k] || '').toLowerCase().includes(paramSearchTerm.toLowerCase())
    );

    return (
      <div className="assembly-param-group">
        <div className={`assembly-param-group-header ${isCollapsed ? 'assembly-param-group-header-collapsed' : 'assembly-param-group-header-expanded'}`}>
          <button
            onClick={() => toggleGroupCollapse(groupKey)}
            className="assembly-group-toggle-btn"
          >
            {isCollapsed ? (
              <ChevronRightIcon className="tool-icon stroke-icon" style={{ width: '10px', height: '10px' }} />
            ) : (
              <ChevronDownIcon className="tool-icon stroke-icon" style={{ width: '10px', height: '10px' }} />
            )}
          </button>
          <strong className="assembly-group-title-editable" style={{ cursor: 'default' }}>{groupKey}</strong>
          <span className="assembly-param-group-count">({visibleParamKeys.length})</span>
        </div>
        {!isCollapsed && (
          <div className="assembly-param-group-content">
            {visibleParamKeys.length === 0 ? (
              <div className="assembly-param-group-empty">
                {t('assemblyManager.noParameters')}
              </div>
            ) : (
              visibleParamKeys.map(k => renderReadOnlyParamRow(k, allParams[k], allParams))
            )}
          </div>
        )}
      </div>
    );
  };

  const isGroup = (params, key) => {
    return params.Groups && params.Groups[key] && Array.isArray(params.Groups[key]);
  };

  const getGroups = (params) => {
    if (!params.Groups) return [];
    return Object.keys(params.Groups);
  };

  const handleParamDragEnd = (assemblyId, objectIndex, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const assembly = local.find(a => a.id === assemblyId);
    if (!assembly) return;
    const objs = assembly.objects ? deepCopy(assembly.objects) : [{ params: deepCopy(assembly.params || {}) }];
    const params = { ...(objs[objectIndex].params || {}) };

    if (!params.Groups) params.Groups = {};

    const draggedId = active.id;
    const overId = over.id;

    const findParamGroup = (paramId) => {
      for (const [groupName, groupKeys] of Object.entries(params.Groups)) {
        if (Array.isArray(groupKeys) && groupKeys.includes(paramId)) {
          return groupName;
        }
      }
      return null;
    };

    const removeParam = (paramId) => {
      const group = findParamGroup(paramId);
      if (group) {
        params.Groups[group] = params.Groups[group].filter(k => k !== paramId);
      }
    };

    const targetIsGroup = isGroup(params, overId);

    if (targetIsGroup) {
      removeParam(draggedId);
      if (!Array.isArray(params.Groups[overId])) {
        params.Groups[overId] = [];
      }
      if (!params.Groups[overId].includes(draggedId)) {
        params.Groups[overId].push(draggedId);
      }
    } else {
      const sourceGroup = findParamGroup(draggedId);
      removeParam(draggedId);

      let order = Array.isArray(params.order) ? [...params.order] :
        Object.keys(params).filter(k => k !== 'order' && k !== 'Groups');

      order = order.filter(k => k !== draggedId);

      const targetIndex = order.indexOf(overId);
      if (targetIndex !== -1) {
        order.splice(targetIndex, 0, draggedId);
      } else {
        order.push(draggedId);
      }

      params.order = order;
    }

    updateObjectParams(assemblyId, objectIndex, params);
  };

  const filtered = local.filter(a => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (a.name || '').toLowerCase().includes(t) || (a.id || '').toLowerCase().includes(t);
  });

  const selected = local.find(a => a.id === selectedId) || null;
  const isReadOnlyMode = activeTab === 'org' && !isAdmin;

  return (
    <>
      <div className="assembly-header">
        <h2>{t('assemblyManager.title')}</h2>
        <div>
          {firebase && (
            <div className="assembly-badge project-tag">
              {activeTab === 'project' ? t('assemblyManager.projectMode', 'Project') : t('assemblyManager.orgMode', 'Organization')}
            </div>
          )}
          {activeTab === 'org' && firebase && (
            <div className={`assembly-badge ${isAdmin ? 'admin' : 'readonly'}`}>
              {isAdmin ? (
                <>
                  <ShieldIcon className="tool-icon stroke-icon" style={{ width: '0.8rem', height: '0.8rem', marginRight: '4px' }} />
                  {t('assemblyManager.admin', 'Admin')}
                </>
              ) : (
                <>
                  <LockIcon className="tool-icon stroke-icon" style={{ width: '0.8rem', height: '0.8rem', marginRight: '4px' }} />
                  {t('assemblyManager.readonly', 'Read-Only')}
                </>
              )}
            </div>
          )}
          <button onClick={onClose} title={`${t('assemblyManager.close')}\n${t('assemblyManager.closeDesc')}`}>✕</button>
        </div>
      </div>

      {firebase && (
        <div className="assembly-tabs-container">
          <div className="assembly-tabs">
            <button
              className={`assembly-tab-button ${activeTab === 'project' ? 'active' : ''}`}
              onClick={() => setActiveTab('project')}
              title={`${projectName || t('assemblyManager.projectCatalogTab', 'Project Catalog')}\n${t('assemblyManager.projectCatalogTabDesc', 'View and manage assemblies saved in this project catalog')}`}
            >
              <ProjectIcon className="tool-icon stroke-icon" style={{ width: '0.95rem', height: '0.95rem' }} />
              {projectName || t('assemblyManager.projectCatalogTab', 'Project Catalog')}
            </button>
            <button
              className={`assembly-tab-button ${activeTab === 'org' ? 'active' : ''}`}
              onClick={() => setActiveTab('org')}
              title={`${orgId || t('assemblyManager.orgTemplatesTab', 'Organization Templates')}\n${t('assemblyManager.orgTemplatesTabDesc', 'View and manage templates shared across your entire organization')}`}
            >
              <OrgIcon className="tool-icon stroke-icon" style={{ width: '0.95rem', height: '0.95rem' }} />
              {orgId || t('assemblyManager.orgTemplatesTab', 'Organization Templates')}
            </button>
          </div>
          <div>
            {savingStatus === 'saving' && (
              <span className="assembly-saving-status saving">
                <span className="assembly-spinner" style={{ width: '12px', height: '12px', borderWidth: '1.5px', margin: '0 6px 0 0', display: 'inline-block', verticalAlign: 'middle' }}></span>
                {t('assemblyManager.savingStatus', 'Saving...')}
              </span>
            )}
            {savingStatus === 'success' && (
              <span className="assembly-saving-status success">
                <CheckIcon className="tool-icon stroke-icon" style={{ width: '12px', height: '12px', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }} />
                {t('assemblyManager.savedStatus', 'Saved')}
              </span>
            )}
            {savingStatus === 'error' && (
              <span className="assembly-saving-status error">
                ✕ {t('assemblyManager.saveErrorStatus', 'Save Error')}
              </span>
            )}

            {importingStatus === 'importing' && (
              <span className="assembly-saving-status saving">
                <span className="assembly-spinner" style={{ width: '12px', height: '12px', borderWidth: '1.5px', margin: '0 6px 0 0', display: 'inline-block', verticalAlign: 'middle' }}></span>
                {t('assemblyManager.importingStatus', 'Importing...')}
              </span>
            )}
            {importingStatus === 'success' && (
              <span className="assembly-saving-status success">
                <CheckIcon className="tool-icon stroke-icon" style={{ width: '12px', height: '12px', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }} />
                {t('assemblyManager.importedStatus', 'Imported')}
              </span>
            )}
            {importingStatus === 'error' && (
              <span className="assembly-saving-status error">
                ✕ {t('assemblyManager.importErrorStatus', 'Import Error')}
              </span>
            )}

            {publishingStatus === 'publishing' && (
              <span className="assembly-saving-status saving">
                <span className="assembly-spinner" style={{ width: '12px', height: '12px', borderWidth: '1.5px', margin: '0 6px 0 0', display: 'inline-block', verticalAlign: 'middle' }}></span>
                {t('assemblyManager.publishingStatus', 'Publishing...')}
              </span>
            )}
            {publishingStatus === 'success' && (
              <span className="assembly-saving-status success">
                <CheckIcon className="tool-icon stroke-icon" style={{ width: '12px', height: '12px', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }} />
                {t('assemblyManager.publishedStatus', 'Published')}
              </span>
            )}
            {publishingStatus === 'error' && (
              <span className="assembly-saving-status error">
                ✕ {t('assemblyManager.publishErrorStatus', 'Publish Error')}
              </span>
            )}
          </div>
        </div>
      )}


      {/* Read-Only Notice banner for org templates */}
      {isReadOnlyMode && (
        <div className="assembly-read-only-notice">
          <LockIcon className="tool-icon stroke-icon" style={{ width: '1rem', height: '1rem' }} />
          {t('assemblyManager.readonlyNotice', 'You are viewing organization templates. Only organization administrators can modify these templates.')}
        </div>
      )}

      <div className="assemblies-container" style={{ '--sidebar-width': `${sidebarWidth}px` }}>
        <div className="assemblies-sidebar">
          <div className="search-container assembly-create-row" style={{ alignItems: 'center' }}>
            <input
              type="search"
              placeholder={t('assemblyManager.searchAssembliesPlaceholder')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              title={`${t('assemblyManager.searchAssembliesPlaceholder')}\n${t('assemblyManager.searchAssembliesDesc')}`}
            />
            {!isReadOnlyMode && (
              <button
                onClick={() => setIsCreatingAssembly(true)}
                title={`${t('assemblyManager.newAssembly')}\n${t('assemblyManager.newAssemblyDesc')}`}
                className="assembly-plus-btn"
              >
                <AddIcon className="tool-icon stroke-icon" />
              </button>
            )}
          </div>
          <div className="assemblies-list-wrapper">
            {loading ? (
              <div className="assembly-loading-container">
                <div className="assembly-spinner"></div>
                <span>{t('assemblyManager.loadingAssemblies', 'Loading assemblies...')}</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="assemblies-list-empty">
                {activeTab === 'project'
                  ? t('assemblyManager.noProjectAssemblies', 'No assemblies in this project catalog.')
                  : t('assemblyManager.noOrgAssemblies', 'No organization templates available.')}
              </div>
            ) : (
              <ul className="assemblies-list">
                {filtered.map(a => {
                  const assemblyColor = getAssemblyColor(a);
                  const assemblyCategory = getAssemblyCategory(a);
                  const isImported = activeTab === 'org' && projectAssemblies.some(p => p.id === a.id);
                  const isFromOrg = activeTab === 'project' && orgAssemblies.some(o => o.id === a.id);
                  return (
                    <li
                      key={a.id}
                      onClick={() => setSelectedId(a.id)}
                      className={`assemblies-list-item ${selectedId === a.id ? 'selected' : ''}`}
                      title={`${a.name || t('assemblyManager.untitled')}\n${t('assemblyManager.selectAssemblyDesc')}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%' }}>
                        <div className="assemblies-list-item-name">{a.name || t('assemblyManager.untitled')}</div>
                        {assemblyColor && (
                          <span
                            className="assembly-color-rect-indicator"
                            style={{
                              backgroundColor: assemblyColor,
                            }}
                            title={`${t('assemblyManager.colorLabel', 'Color')}: ${assemblyColor}`}
                          />
                        )}
                      </div>
                      <div className="assembly-item-category" style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        <span>{assemblyCategory || t('assemblyManager.noCategory', 'No Category')}</span>
                        {isImported && (
                          <span className="assembly-status-badge imported" title={t('assemblyManager.importedInProjectTooltip', 'Imported in this project')}>
                            {t('assemblyManager.inProjectBadge', 'In Project')}
                          </span>
                        )}
                        {isFromOrg && (
                          <span className="assembly-status-badge from-org" title={t('assemblyManager.fromOrgTemplatesTooltip', 'From Organization templates')}>
                            {t('assemblyManager.templateBadge', 'Template')}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="assemblies-main-content">
          {loading ? (
            <div className="assembly-loading-container">
              <div className="assembly-spinner"></div>
              <span>{t('assemblyManager.fetchingParameters', 'Fetching parameters...')}</span>
            </div>
          ) : isCreatingAssembly ? (
            <div className="assembly-create-panel">
              <h4>{t('assemblyManager.createAssembly', 'Create New Assembly')}</h4>
              <div className="assembly-create-row">
                <input
                  type="text"
                  placeholder={t('assemblyManager.assemblyNamePlaceholder')}
                  value={newAssemblyName}
                  onChange={(e) => setNewAssemblyName(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddAssembly();
                    }
                    if (e.key === 'Escape') handleCancelCreateAssembly();
                  }}
                  title={`${t('assemblyManager.assemblyNamePlaceholder')}\n${t('assemblyManager.assemblyNameInputDesc')}`}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleAddAssembly}
                  className="premium-btn publish"
                  title={`${t('create')}\n${t('assemblyManager.newAssemblyDesc')}`}
                >
                  {t('create')}
                </button>
                <button
                  type="button"
                  onClick={handleCancelCreateAssembly}
                  className="premium-btn secondary"
                  title={`${t('cancel')}\n${t('assemblyManager.cancelAddDesc')}`}
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          ) : (
            <>
              {selected && (
                <div className="search-container assembly-param-search-bar">
                  <input
                    type="search"
                    placeholder={t('assemblyManager.searchParametersPlaceholder')}
                    value={paramSearchTerm}
                    onChange={e => setParamSearchTerm(e.target.value)}
                    title={`${t('assemblyManager.searchParametersPlaceholder')}\n${t('assemblyManager.searchParametersDesc')}`}
                  />
                  <div className="assembly-param-actions">
                    {firebase && (
                      activeTab === 'project' ? (
                        <button
                          className="premium-btn publish"
                          onClick={() => handlePublishToOrganization(selected)}
                          disabled={publishingStatus !== 'idle'}
                          title={`${t('assemblyManager.publishToOrg')}\n${t('assemblyManager.publishToOrgDesc')}`}
                        >
                          <PublishIcon className="tool-icon stroke-icon" style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                          {t('assemblyManager.publish', 'Publish')}
                        </button>
                      ) : (
                        <button
                          className="premium-btn import"
                          onClick={() => handleImportToProject(selected)}
                          disabled={importingStatus !== 'idle'}
                          title={`${t('assemblyManager.importToProject')}\n${t('assemblyManager.importToProjectDesc')}`}
                        >
                          <ImportIcon className="tool-icon stroke-icon" style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                          {t('assemblyManager.import', 'Import')}
                        </button>
                      )
                    )}
                    {!isReadOnlyMode && (
                      <>
                        <button
                          onClick={() => handleSaveEdited(selected)}
                          title={`${t('save')}\n${t('assemblyManager.saveDesc')}`}
                          className="premium-btn import"
                        >
                          <CheckIcon className="tool-icon stroke-icon" style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                          {t('save')}
                        </button>
                        <button
                          onClick={() => handleDelete(selected.id)}
                          title={`${t('delete')}\n${t('assemblyManager.deleteDesc')}`}
                          className="premium-btn danger"
                        >
                          <DeleteIcon className="tool-icon stroke-icon" style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                          {t('delete')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {!selected ? (
                <div className="empty-state-container">
                  <div className="empty-state-icon">
                    <TagIcon className="tool-icon stroke-icon" style={{ width: '3rem', height: '3rem', opacity: 0.6 }} />
                  </div>
                  <div className="empty-state-title">{t('assemblyManager.selectAssemblyTitle', 'No Assembly Selected')}</div>
                  <div className="empty-state-desc">{t('assemblyManager.selectAssemblyMessage')}</div>
                </div>
              ) : (
                <div className="assembly-editor">
                  {selected.objects && selected.objects.length > 0 ? (
                    selected.objects.map((obj, idx) => (
                      <div key={obj.id || idx} className="object-block">
                        <div className="object-header">
                          {isReadOnlyMode ? (
                            <strong style={{ fontSize: '0.95rem', flex: 1, padding: '4px 0' }}>{obj.name || t('assemblyManager.parametersLabel', 'Parameters')}</strong>
                          ) : (
                            <input
                              className="object-name"
                              value={obj.name || ''}
                              onChange={e => {
                                const name = e.target.value;
                                setLocal(prev => prev.map(a => {
                                  if (a.id !== selected.id) return a;
                                  const objs = (a.objects || []).map((o, i) => i === idx ? { ...o, name } : o);
                                  return { ...a, objects: objs };
                                }));
                              }}
                            />
                          )}
                          {!isReadOnlyMode && (
                            <div>
                              {addingParamFor === `obj_${idx}` ? (
                                <>
                                  <input
                                    type="text"
                                    placeholder={t('assemblyManager.paramKeyPlaceholder')}
                                    value={newParamInputs.key}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setNewParamInputs({ ...newParamInputs, key: e.target.value })}
                                    onKeyDown={(e) => {
                                      e.stopPropagation();
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddParam(selected.id, idx);
                                      }
                                      if (e.key === 'Escape') {
                                        setAddingParamFor(null);
                                        setNewParamInputs({ key: '', value: '' });
                                      }
                                    }}
                                    className="assembly-param-input-key"
                                    autoFocus
                                  />
                                  <input
                                    type="text"
                                    placeholder={t('assemblyManager.paramValuePlaceholder')}
                                    value={newParamInputs.value}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => setNewParamInputs({ ...newParamInputs, value: e.target.value })}
                                    onKeyDown={(e) => {
                                      e.stopPropagation();
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddParam(selected.id, idx);
                                      }
                                      if (e.key === 'Escape') {
                                        setAddingParamFor(null);
                                        setNewParamInputs({ key: '', value: '' });
                                      }
                                    }}
                                    className="assembly-param-input-val"
                                  />
                                  <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddParam(selected.id, idx); }} title={`${t('save')}\n${t('assemblyManager.confirmAddDesc')}`}>
                                    <CheckIcon className="tool-icon stroke-icon" style={{ width: '0.85rem', height: '0.85rem', color: '#10b981', display: 'inline-block', verticalAlign: 'middle' }} />
                                  </button>
                                  <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAddingParamFor(null); setNewParamInputs({ key: '', value: '' }); }} className="delete-button" title={`${t('cancel')}\n${t('assemblyManager.cancelAddDesc')}`}>✕</button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => { setAddingParamFor(`obj_${idx}`); setNewParamInputs({ key: '', value: '' }); }} title={`${t('assemblyManager.addParam')}\n${t('assemblyManager.addParamDesc')}`}>
                                    <AddIcon className="tool-icon stroke-icon" style={{ width: '0.75rem', height: '0.75rem', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }} />
                                    {t('assemblyManager.paramShort', 'Param')}
                                  </button>
                                  {addingGroupFor === `obj_${idx}` ? (
                                    <>
                                      <input
                                        type="text"
                                        placeholder={t('assemblyManager.groupNamePlaceholder')}
                                        value={newGroupName}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => setNewGroupName(e.target.value)}
                                        onKeyDown={(e) => {
                                          e.stopPropagation();
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddGroup(selected.id, idx);
                                          }
                                          if (e.key === 'Escape') {
                                            setAddingGroupFor(null);
                                            setNewGroupName('');
                                          }
                                        }}
                                        className="assembly-group-input"
                                        autoFocus
                                      />
                                      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddGroup(selected.id, idx); }} title={`${t('create')}\n${t('assemblyManager.confirmAddDesc')}`}>
                                        <CheckIcon className="tool-icon stroke-icon" style={{ width: '0.85rem', height: '0.85rem', color: '#10b981', display: 'inline-block', verticalAlign: 'middle' }} />
                                      </button>
                                      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAddingGroupFor(null); setNewGroupName(''); }} className="delete-button" title={`${t('cancel')}\n${t('assemblyManager.cancelAddDesc')}`}>✕</button>
                                    </>
                                  ) : (
                                    <button onClick={() => { setAddingGroupFor(`obj_${idx}`); setNewGroupName(''); }} title={`${t('assemblyManager.addGroup')}\n${t('assemblyManager.addGroupDesc')}`}>
                                      <AddIcon className="tool-icon stroke-icon" style={{ width: '0.75rem', height: '0.75rem', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }} />
                                      {t('assemblyManager.groupShort', 'Group')}
                                    </button>
                                  )}
                                </>
                              )}
                              <button onClick={() => handleDeleteObject(selected.id, idx)} className="delete-button" title={`${t('delete')}\n${t('assemblyManager.deleteObjectDesc')}`}>✕</button>
                            </div>
                          )}
                        </div>

                        <div className="params-list">
                          {(!obj.params || Object.keys(obj.params).length === 0) && <div className="empty">{t('assemblyManager.noParameters')}</div>}

                          {obj.params && (() => {
                            const allKeys = Object.keys(obj.params).filter(k => k !== 'order' && k !== 'Groups');
                            const orderArray = Array.isArray(obj.params.order) ? obj.params.order : [];
                            const orderedKeys = [...orderArray.filter(k => allKeys.includes(k) || isGroup(obj.params, k)), ...allKeys.filter(k => !orderArray.includes(k))];

                            const paramsInGroups = new Set();
                            if (obj.params.Groups) {
                              Object.values(obj.params.Groups).forEach(groupKeys => {
                                if (Array.isArray(groupKeys)) {
                                  groupKeys.forEach(paramKey => paramsInGroups.add(paramKey));
                                }
                              });
                            }

                            const groups = getGroups(obj.params);
                            const regularParams = orderedKeys.filter(k => !isGroup(obj.params, k) && !paramsInGroups.has(k));

                            const visibleRegularParams = regularParams.filter(k =>
                              (k || '').toLowerCase().includes(paramSearchTerm.toLowerCase()) ||
                              String(obj.params[k] || '').toLowerCase().includes(paramSearchTerm.toLowerCase())
                            );

                            if (isReadOnlyMode) {
                              return (
                                <>
                                  {orderedKeys.map((k) => {
                                    if (isGroup(obj.params, k)) {
                                      return (
                                        <ReadOnlyGroup
                                          key={k}
                                          groupKey={k}
                                          groupParamKeys={obj.params.Groups[k]}
                                          allParams={obj.params}
                                        />
                                      );
                                    }
                                    if (!visibleRegularParams.includes(k)) return null;
                                    return renderReadOnlyParamRow(k, obj.params[k], obj.params);
                                  })}
                                </>
                              );
                            }

                            return (
                              <DndContext collisionDetection={closestCenter} onDragEnd={(e) => handleParamDragEnd(selected.id, idx, e)}>
                                <SortableContext items={orderedKeys} strategy={verticalListSortingStrategy}>
                                  {orderedKeys.length === 0 ? (
                                    <div className="empty">{t('assemblyManager.noParameters')}</div>
                                  ) : (
                                    <>
                                      {orderedKeys.map((k) => {
                                        if (isGroup(obj.params, k)) {
                                          return (
                                            <DroppableGroup
                                              key={k}
                                              groupKey={k}
                                              groupParamKeys={obj.params.Groups[k]}
                                              assemblyId={selected.id}
                                              objectIndex={idx}
                                              allParams={obj.params}
                                            />
                                          );
                                        }
                                        if (!visibleRegularParams.includes(k)) return null;
                                        const v = obj.params[k];
                                        return renderParamRow(selected.id, idx, k, v, obj.params);
                                      })}
                                    </>
                                  )}
                                </SortableContext>
                              </DndContext>
                            );
                          })()}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="object-block">
                      <div className="object-header">
                        {isReadOnlyMode ? (
                          <strong style={{ fontSize: '1.1rem', flex: 1, padding: '4px 0' }}>{selected.name || ''}</strong>
                        ) : isLegacyAssembly(selected, firebase) ? (
                          <strong style={{ fontSize: '1.1rem', flex: 1, padding: '4px 0' }}>
                            {selected.name || ''}
                            <span
                              style={{
                                fontSize: '0.7rem',
                                marginLeft: '8px',
                                opacity: 0.6,
                                fontStyle: 'italic',
                                fontWeight: 'normal',
                              }}
                              title="Legacy assembly — cannot be renamed"
                            >
                              (legacy)
                            </span>
                          </strong>
                        ) : isRenamingAssembly ? (
                          <input
                            type="text"
                            value={selected.name || ''}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              // Normalize the new name to keep params.Assembly in sync
                              // with the doc ID immediately in local state
                              const normalized =
                                firebase &&
                                typeof firebase.normalizeAssemblyId === 'function'
                                  ? firebase.normalizeAssemblyId(newValue)
                                  : newValue.trim();
                              const currentParams = { ...(selected.params || {}) };
                              if (normalized) {
                                currentParams.Assembly = normalized;
                                // Keep Assembly in the order array so it does not
                                // jump to the end of the parameter list.
                                const order = Array.isArray(currentParams.order) ? [...currentParams.order] : [];
                                if (!order.includes('Assembly')) order.unshift('Assembly');
                                currentParams.order = order;
                              }
                              updateAssembly(selected.id, { name: newValue, params: currentParams });
                            }}
                            onBlur={() => setIsRenamingAssembly(false)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === 'Escape') {
                                setIsRenamingAssembly(false);
                              }
                            }}
                            autoFocus
                            style={{ width: 'auto', maxHeight: '1.5rem', padding: '0.1rem 0.5rem', margin: '0', fontSize: '1.2rem' }}
                          />
                        ) : (
                          <h3
                            onClick={() => {
                              if (isLegacyAssembly(selected, firebase)) {
                                alert(t('assemblyManager.legacyRenameBlocked', 'Legacy assemblies cannot be renamed.'));
                                return;
                              }
                              setIsRenamingAssembly(true);
                            }}
                            style={{ cursor: 'pointer' }}
                            title={
                              isLegacyAssembly(selected, firebase)
                                ? t('assemblyManager.legacyCannotRename', 'Legacy assembly — cannot be renamed')
                                : t('assemblyManager.clickToRename')
                            }
                          >
                            {selected.name || ''}
                          </h3>
                        )}
                        {!isReadOnlyMode && (
                          <>
                            {addingParamFor === 'flat' ? (
                              <div>
                                <input
                                  type="text"
                                  placeholder={t('assemblyManager.paramKeyPlaceholder')}
                                  value={newParamInputs.key}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => setNewParamInputs({ ...newParamInputs, key: e.target.value })}
                                  onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleAddParam(selected.id, 0); } if (e.key === 'Escape') { setAddingParamFor(null); setNewParamInputs({ key: '', value: '' }); } }}
                                  className="assembly-param-input-key"
                                  autoFocus
                                />
                                <input
                                  type="text"
                                  placeholder={t('assemblyManager.paramValuePlaceholder')}
                                  value={newParamInputs.value}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => setNewParamInputs({ ...newParamInputs, value: e.target.value })}
                                  onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); handleAddParam(selected.id, 0); } if (e.key === 'Escape') { setAddingParamFor(null); setNewParamInputs({ key: '', value: '' }); } }}
                                  className="assembly-param-input-val"
                                />
                                <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddParam(selected.id, 0); }} title={`${t('save')}\n${t('assemblyManager.confirmAddDesc')}`}>
                                  <CheckIcon className="tool-icon stroke-icon" style={{ width: '0.85rem', height: '0.85rem', color: '#10b981', display: 'inline-block', verticalAlign: 'middle' }} />
                                </button>
                                <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAddingParamFor(null); setNewParamInputs({ key: '', value: '' }); }} className="delete-button" title={`${t('cancel')}\n${t('assemblyManager.cancelAddDesc')}`}>✕</button>
                              </div>
                            ) : (
                              <>
                                <button onClick={() => { setAddingParamFor('flat'); setNewParamInputs({ key: '', value: '' }); }} title={`${t('assemblyManager.addParam')}\n${t('assemblyManager.addParamDesc')}`}>
                                  <AddIcon className="tool-icon stroke-icon" style={{ width: '0.75rem', height: '0.75rem', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }} />
                                  {t('assemblyManager.addParam')}
                                </button>
                                {addingGroupFor === 'flat' ? (
                                  <>
                                    <input
                                      type="text"
                                      placeholder={t('assemblyManager.groupNamePlaceholder')}
                                      value={newGroupName}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => setNewGroupName(e.target.value)}
                                      onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleAddGroup(selected.id, 0);
                                        }
                                        if (e.key === 'Escape') {
                                          setAddingGroupFor(null);
                                          setNewGroupName('');
                                        }
                                      }}
                                      className="assembly-group-input"
                                      autoFocus
                                    />
                                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAddGroup(selected.id, 0); }} title={`${t('create')}\n${t('assemblyManager.confirmAddDesc')}`}>
                                      <CheckIcon className="tool-icon stroke-icon" style={{ width: '0.85rem', height: '0.85rem', color: '#10b981', display: 'inline-block', verticalAlign: 'middle' }} />
                                    </button>
                                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAddingGroupFor(null); setNewGroupName(''); }} className="delete-button" title={`${t('cancel')}\n${t('assemblyManager.cancelAddDesc')}`}>✕</button>
                                  </>
                                ) : (
                                  <button onClick={() => { setAddingGroupFor('flat'); setNewGroupName(''); }} title={`${t('assemblyManager.addGroup')}\n${t('assemblyManager.addGroupDesc')}`}>
                                    <AddIcon className="tool-icon stroke-icon" style={{ width: '0.75rem', height: '0.75rem', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }} />
                                    {t('assemblyManager.addGroup')}
                                  </button>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                      <div className="params-list">
                        {(selected.params && Object.keys(selected.params).length === 0) && <div className="empty">{t('assemblyManager.noParameters')}</div>}

                        {selected.params && (() => {
                          const allKeys = Object.keys(selected.params).filter(k => k !== 'order' && k !== 'Groups');
                          const orderArray = Array.isArray(selected.params.order) ? selected.params.order : [];
                          const orderedKeys = [...orderArray.filter(k => allKeys.includes(k) || isGroup(selected.params, k)), ...allKeys.filter(k => !orderArray.includes(k))];

                          const paramsInGroups = new Set();
                          if (selected.params.Groups) {
                            Object.values(selected.params.Groups).forEach(groupKeys => {
                              if (Array.isArray(groupKeys)) {
                                groupKeys.forEach(paramKey => paramsInGroups.add(paramKey));
                              }
                            });
                          }

                          const regularParams = orderedKeys.filter(k => !isGroup(selected.params, k) && !paramsInGroups.has(k));
                          const visibleRegularParams = regularParams.filter(k =>
                            (k || '').toLowerCase().includes(paramSearchTerm.toLowerCase()) ||
                            String(selected.params[k] || '').toLowerCase().includes(paramSearchTerm.toLowerCase())
                          );

                          if (isReadOnlyMode) {
                            return (
                              <>
                                {orderedKeys.map((k) => {
                                  if (isGroup(selected.params, k)) {
                                    return (
                                      <ReadOnlyGroup
                                        key={k}
                                        groupKey={k}
                                        groupParamKeys={selected.params.Groups[k]}
                                        allParams={selected.params}
                                      />
                                    );
                                  }
                                  if (!visibleRegularParams.includes(k)) return null;
                                  return renderReadOnlyParamRow(k, selected.params[k], selected.params);
                                })}
                              </>
                            );
                          }

                          return (
                            <DndContext collisionDetection={closestCenter} onDragEnd={(e) => handleParamDragEnd(selected.id, 0, e)}>
                              <SortableContext items={orderedKeys} strategy={verticalListSortingStrategy}>
                                {orderedKeys.length === 0 ? (
                                  <div className="empty">{t('assemblyManager.noParameters')}</div>
                                ) : (
                                  <>
                                    {orderedKeys.map((k) => {
                                      if (isGroup(selected.params, k)) {
                                        return (
                                          <DroppableGroup
                                            key={k}
                                            groupKey={k}
                                            groupParamKeys={selected.params.Groups[k]}
                                            assemblyId={selected.id}
                                            objectIndex={0}
                                            allParams={selected.params}
                                          />
                                        );
                                      }
                                      if (!visibleRegularParams.includes(k)) return null;
                                      const v = selected.params[k];
                                      return renderParamRow(selected.id, 0, k, v, selected.params);
                                    })}
                                  </>
                                )}
                              </SortableContext>
                            </DndContext>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AssemblyManager;